/**
 * 📋 SDLC 6.5.0 UNIFIED SPECIFICATION STANDARD FILE HEADER
 * ===============================================================================
 * FILE: frontend/src/pages/SsoComplete.tsx
 * PURPOSE: Nhận vé dùng-một-lần từ SSO callback, đổi lấy token, đưa vào ứng dụng.
 * SDLC FRAMEWORK VERSION: 6.5.0 (7-Pillar + 9 Mental Models + LRAP)
 * DATE: 2026-08-04
 * COMPLIANCE: SDLC 6.5.0 + DD-0090 (SSO thin slice)
 * PILLAR: P1 (Stage 06 - BUILD) + P4 (Quality Gates - Security)
 *
 * @business-context
 * - Domain: Đăng nhập SSO — mắt xích cuối đưa người dùng vào hệ thống
 * - Impact: CRITICAL — không có trang này, SSO chạy xong vẫn trả JSON thô
 *
 * @technical-architecture
 * - Vì sao có vé thay vì token trong URL: audit @cso (audit/cso-token-in-url) đo
 *   được 2.459 hit token-in-URL đang chảy vào log nginx-proxy-manager
 *   world-readable. URL chỉ mang vé mờ, TTL 60s, dùng-một-lần; token thật đi
 *   trong thân phản hồi POST — thứ không vào log truy cập.
 * - Vé đã dùng => 401 => quay lại /login. Lộ vé sau khi dùng là vô giá trị.
 *
 * @cross-references
 * - Backend: backend/app/api/v1/auth_sso.py (callback 303 + POST /sso/exchange)
 * - Luồng mật khẩu tương ứng: frontend/src/pages/Login.tsx
 * ===============================================================================
 */
import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../store/auth";
import apiWrapper from "../services/api/apiWrapper";
import { getDashboardRouteForRole } from "../utils/roleDashboardMap";

export const SsoComplete: React.FC = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, setTokens, setTenantAuth } = useAuthStore();
  const [error, setError] = useState<string | null>(null);
  // Vé dùng-một-lần: React StrictMode gọi effect hai lần ở dev, lần thứ hai sẽ
  // nhận 401 vì vé đã tiêu. Chốt để chỉ đổi đúng một lần.
  const claimed = useRef(false);

  useEffect(() => {
    const ticket = params.get("ticket");
    if (!ticket) {
      setError("Thiếu vé đăng nhập.");
      return;
    }
    if (claimed.current) return;
    claimed.current = true;

    (async () => {
      try {
        const res = await fetch("/api/v1/auth/sso/exchange", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ticket }),
        });
        if (!res.ok) {
          setError("Vé đăng nhập không hợp lệ hoặc đã hết hạn. Vui lòng đăng nhập lại.");
          return;
        }
        const d = await res.json();

        setTokens(d.access_token, d.refresh_token);

        // BẮT BUỘC — không có dòng này thì userRole = null và TenantAdminLayout
        // đẩy sang /dashboard/staff rồi bật về /login mà KHÔNG báo lỗi (đo 04/08).
        // Luồng mật khẩu (Login.tsx) gọi đúng ba hàm: setTokens + setTenantAuth + setUser.
        setTenantAuth(
          d.tenant_key || "",
          d.capabilities || [],
          d.role || d.user.role,
          d.home_route ?? null,
        );
        if (d.tenant_key) apiWrapper.setTenantKey(d.tenant_key);
        setUser({
          id: d.user.id,
          email: d.user.email,
          full_name: d.user.full_name,
          role: d.user.role,
          is_active: true,
          // ISS-PILOT-12 (2026-08-09): cùng gap với Login.tsx — /profile hiện
          // "—" cho user đăng nhập qua SSO nếu thiếu field này.
          employee_code: d.user.employee_code,
          // 2026-08-25: map position cho SC-019-EMP-HOME header parity.
          position: d.user.position,
        });
        navigate(d.home_route || getDashboardRouteForRole(d.user.role), { replace: true });
      } catch {
        setError("Không kết nối được máy chủ. Vui lòng thử lại.");
      }
    })();
  }, [params, navigate, setTokens, setUser, setTenantAuth]);

  if (error) {
    return (
      <main style={{ display: "grid", placeItems: "center", minHeight: "60vh", padding: "1rem" }}>
        <div style={{ textAlign: "center", maxWidth: "28rem" }}>
          <p style={{ marginBottom: "1rem" }}>{error}</p>
          <a href="/login">Quay lại đăng nhập</a>
        </div>
      </main>
    );
  }

  return (
    <main style={{ display: "grid", placeItems: "center", minHeight: "60vh" }}>
      <p>Đang hoàn tất đăng nhập…</p>
    </main>
  );
};

export default SsoComplete;
